import { useQuery } from '@tanstack/react-query'
import { CalendarDays, ChevronLeft, ChevronRight, Download, Eye, Facebook, Link2, Mail, Printer, Share2, X } from 'lucide-react'
import { useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { PageHero } from '../components/Common'
import type { NewsCategory } from '../types/content'
import { api } from '../services/api'

const categories: Array<'Tất cả' | NewsCategory> = [
  'Tất cả',
  'Tin tức nhà trường',
  'Hoạt động chuyên môn',
  'Hoạt động đoàn đội',
  'Gương người tốt - Việc tốt',
  'Sự kiện',
]

const mediaURL = (url?: string) => {
  if (!url) return '#'
  if (/^https?:\/\//i.test(url) || url.startsWith('/assets/')) return url
  return `${import.meta.env.VITE_CMS_URL || 'http://localhost:3001'}${url}`
}

export function NewsListPage() {
  const [params] = useSearchParams()
  const initial = (params.get('category') as NewsCategory | null) || 'Tất cả'
  const [selected, setSelected] = useState<typeof categories[number]>(
    categories.includes(initial as never) ? initial : 'Tất cả'
  )
  const [page, setPage] = useState(1)

  const { data, isLoading, isError } = useQuery({
    queryKey: ['posts', selected, page],
    queryFn: () => api.getPosts(selected, page, 12),
  })

  const posts = data?.data ?? []
  const meta = data?.meta
  const totalPages = meta?.totalPages ?? 1

  return (
    <>
      <PageHero title="TIN TỨC - SỰ KIỆN" />
      <div className="container content-with-sidebar news-page">
        <div className="content-main">
          <div className="category-tabs">
            {categories.map((category) => (
              <button
                key={category}
                className={selected === category ? 'active' : ''}
                onClick={() => { setSelected(category); setPage(1) }}
              >
                {category}
              </button>
            ))}
          </div>
          <div className="news-list">
            {isLoading && <p>Đang tải tin tức...</p>}
            {isError && <p>Không tải được dữ liệu. Vui lòng thử lại.</p>}
            {!isLoading && !isError && posts.length === 0 && <p>Không có tin tức nào.</p>}
            {posts.map((item) => (
              <article key={item.id}>
                <img src={item.image || '/assets/images/news-opening.jpg'} alt={item.title} />
                <div>
                  <span className="badge">{item.category}</span>
                  <h2><Link to={`/tin-tuc/${item.slug}`}>{item.title}</Link></h2>
                  <p>{item.excerpt}</p>
                  <div className="meta">
                    <span><CalendarDays />{item.date ? new Date(item.date).toLocaleDateString('vi-VN') : ''}</span>
                    <span><Eye />{(item.views ?? 0).toLocaleString('vi-VN')} lượt xem</span>
                    <Link to={`/tin-tuc/${item.slug}`}>Đọc tiếp →</Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
          {totalPages > 1 && (
            <div className="pagination">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button key={p} className={page === p ? 'active' : ''} onClick={() => setPage(p)}>{p}</button>
              ))}
              {page < totalPages && (
                <button onClick={() => setPage(p => p + 1)}>Tiếp theo →</button>
              )}
            </div>
          )}
        </div>
        <NewsSidebar />
      </div>
    </>
  )
}

function NewsSidebar() {
  const { data } = useQuery({
    queryKey: ['posts-sidebar'],
    queryFn: () => api.getPosts('Tất cả', 1, 5),
  })
  const featured = data?.data ?? []

  return (
    <aside className="sidebar">
      <div className="sidebar-box">
        <h3>DANH MỤC TIN TỨC</h3>
        {categories.map((category) => (
          <a href="#" key={category}><span>{category}</span></a>
        ))}
      </div>
      <div className="sidebar-box">
        <h3>TIN NỔI BẬT</h3>
        {featured.map((item) => (
          <Link to={`/tin-tuc/${item.slug}`} key={item.id} className="side-news">
            <img src={item.image || '/assets/images/news-opening.jpg'} alt="" />
            <div>
              <strong>{item.title}</strong>
              <small>{item.date ? new Date(item.date).toLocaleDateString('vi-VN') : ''}</small>
            </div>
          </Link>
        ))}
      </div>
      <div className="sidebar-box tag-box">
        <h3>TỪ KHÓA</h3>
        <div>
          {['Khai giảng', 'Kỹ năng sống', 'Học sinh giỏi', 'Ngoại khóa', 'Đoàn đội', 'Thi đua', 'Bóng đá', 'Kế hoạch nhỏ', 'Tiếp sức đến trường', 'STEM'].map((tag) => (
            <span key={tag}>{tag}</span>
          ))}
        </div>
      </div>
      <div className="sidebar-box newsletter">
        <h3>ĐĂNG KÝ NHẬN TIN</h3>
        <p>Đăng ký để nhận những tin tức mới nhất từ nhà trường</p>
        <input placeholder="Nhập email của bạn..." />
        <button>Đăng ký</button>
      </div>
    </aside>
  )
}

export function NewsDetailPage() {
  const { slug } = useParams()
  const [viewerIndex, setViewerIndex] = useState<number | null>(null)
  const { data: item, isLoading, isError } = useQuery({
    queryKey: ['post', slug],
    queryFn: () => api.getPost(slug || ''),
    enabled: Boolean(slug),
  })

  const { data: relatedData } = useQuery({
    queryKey: ['posts-related'],
    queryFn: () => api.getPosts('Tất cả', 1, 6),
  })
  const related = (relatedData?.data ?? []).filter((e) => e.id !== item?.id).slice(0, 4)

  if (isLoading) return (
    <>
      <PageHero title="TIN TỨC - SỰ KIỆN" />
      <div className="container generic-page"><p>Đang tải bài viết...</p></div>
    </>
  )
  if (isError || !item) return (
    <>
      <PageHero title="TIN TỨC - SỰ KIỆN" />
      <div className="container generic-page"><p>Không tìm thấy bài viết.</p></div>
    </>
  )

  const coverImage = item.image || '/assets/images/opening-detail.jpg'
  const galleryImages = (item.gallery ?? []).map((img, index) => ({
    id: img.id || `gallery-${index}`,
    url: img.url || coverImage,
    alt: img.alt || item.title,
  }))
  const articleImages = [
    { id: 'cover', url: coverImage, alt: item.title },
    ...galleryImages,
  ].filter((img, index, images) => img.url && images.findIndex((entry) => entry.url === img.url) === index)
  const activeViewerImage = viewerIndex === null ? null : articleImages[viewerIndex]
  const canStepViewer = articleImages.length > 1
  const openViewer = (index: number) => setViewerIndex(index)
  const closeViewer = () => setViewerIndex(null)
  const stepViewer = (direction: -1 | 1) => {
    if (!articleImages.length) return
    setViewerIndex((index) => {
      const currentIndex = index ?? 0
      return (currentIndex + direction + articleImages.length) % articleImages.length
    })
  }

  return (
    <>
      <div className="container breadcrumb-line">
        <Link to="/">Trang chủ</Link><span>›</span>
        <Link to="/tin-tuc">Tin tức - Sự kiện</Link><span>›</span>
        <span>Chi tiết</span>
      </div>
      <div className="container content-with-sidebar news-detail">
        <article className="article-detail">
          <span className="badge">{item.category}</span>
          <h1>{item.title}</h1>
          <div className="article-head-meta">
            <div>
              <span><CalendarDays />{item.date ? new Date(item.date).toLocaleDateString('vi-VN') : ''}</span>
              <span><Eye />{(item.views ?? 0).toLocaleString('vi-VN')} lượt xem</span>
            </div>
            <div className="share">
              <span>Chia sẻ:</span>
              <button><Facebook /></button>
              <button><Share2 /></button>
              <button><Mail /></button>
              <button><Printer /></button>
            </div>
          </div>
          <button type="button" className="article-cover-button" onClick={() => openViewer(0)} aria-label={`Xem ảnh ${item.title}`}>
            <img className="article-cover" src={mediaURL(coverImage)} alt={item.title} />
          </button>
          {item.gallery && item.gallery.length > 0 && (
            <div className="thumb-strip">
              {galleryImages.map((img, index) => (
                <button
                  type="button"
                  key={img.id || index}
                  onClick={() => openViewer(Math.max(0, articleImages.findIndex((entry) => entry.url === img.url)))}
                  aria-label={`Xem ảnh ${index + 1} trong album`}
                >
                  <img src={mediaURL(img.url)} alt={img.alt} />
                </button>
              ))}
            </div>
          )}
          <div className="article-body">
            {/* content từ Payload CMS là rich text, render excerpt nếu không có rich text */}
            {item.excerpt ? <p>{String(item.excerpt)}</p> : null}
            {item.content && typeof item.content === 'object'
              ? <RichTextRenderer content={item.content} />
              : null}
          </div>
          {/* Tệp đính kèm */}
          {item.attachments && item.attachments.length > 0 && (
            <div className="attachment">
              <h3><Link2 />Tệp đính kèm</h3>
              {item.attachments.map((att) => (
                <a href={att.url || '#'} key={att.id} download>
                  <span className="file-icon">{att.mimeType?.includes('pdf') ? 'PDF' : 'FILE'}</span>
                  <div>
                    <strong>{att.filename || 'Tải xuống'}</strong>
                    {att.filesize && <small>{(att.filesize / 1024).toFixed(0)} KB</small>}
                  </div>
                  <Download />
                </a>
              ))}
            </div>
          )}
          {related.length >= 2 && (
            <div className="article-nav">
              <Link to={`/tin-tuc/${related[0].slug}`}>
                ← <span>Bài viết trước<strong>{related[0].title}</strong></span>
              </Link>
              <Link to={`/tin-tuc/${related[1].slug}`}>
                <span>Bài viết tiếp theo<strong>{related[1].title}</strong></span> →
              </Link>
            </div>
          )}
        </article>
        <aside className="sidebar">
          <div className="sidebar-box">
            <h3>TIN TỨC LIÊN QUAN</h3>
            {related.map((entry) => (
              <Link to={`/tin-tuc/${entry.slug}`} key={entry.id} className="side-news">
                <img src={entry.image || '/assets/images/news-opening.jpg'} alt="" />
                <div>
                  <strong>{entry.title}</strong>
                  <small>{entry.date ? new Date(entry.date).toLocaleDateString('vi-VN') : ''}</small>
                </div>
              </Link>
            ))}
          </div>
          <NewsSidebarCompact />
        </aside>
      </div>
      {activeViewerImage && (
        <div className="library-viewer" role="dialog" aria-modal="true" aria-label={item.title}>
          <div className="library-viewer__panel">
            <div className="library-viewer__header">
              <div>
                <h2>{item.title}</h2>
                <p>{(viewerIndex ?? 0) + 1}/{articleImages.length}</p>
              </div>
              <button type="button" onClick={closeViewer} aria-label="Đóng"><X /></button>
            </div>

            <div className="library-viewer__stage">
              {canStepViewer && (
                <button type="button" className="library-viewer__nav library-viewer__nav--prev" onClick={() => stepViewer(-1)} aria-label="Trước"><ChevronLeft /></button>
              )}
              <img src={mediaURL(activeViewerImage.url)} alt={activeViewerImage.alt} />
              {canStepViewer && (
                <button type="button" className="library-viewer__nav library-viewer__nav--next" onClick={() => stepViewer(1)} aria-label="Tiếp"><ChevronRight /></button>
              )}
            </div>

            {articleImages.length > 1 && (
              <div className="library-viewer__thumbs">
                {articleImages.map((img, index) => (
                  <button type="button" className={index === viewerIndex ? 'active' : ''} key={`${img.url}-${index}`} onClick={() => setViewerIndex(index)}>
                    <img src={mediaURL(img.url)} alt={img.alt} />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )
}

// Simple rich text renderer cho Payload CMS Lexical/Slate content
function RichTextRenderer({ content }: { content: unknown }) {
  // Payload CMS dùng Lexical, content là JSON với root.children
  try {
    const data = content as { root?: { children?: unknown[] } }
    if (data?.root?.children) {
      return (
        <div className="rich-text">
          {data.root.children.map((node: any, i) => renderNode(node, i))}
        </div>
      )
    }
  } catch {}
  return null
}

function renderNode(node: any, key: number): React.ReactNode {
  if (!node) return null
  switch (node.type) {
    case 'paragraph':
      return <p key={key}>{node.children?.map((c: any, i: number) => renderLeaf(c, i))}</p>
    case 'heading':
      const Tag = `h${node.tag?.replace('h', '') || '2'}` as 'h1' | 'h2' | 'h3' | 'h4'
      return <Tag key={key}>{node.children?.map((c: any, i: number) => renderLeaf(c, i))}</Tag>
    case 'list':
      return node.listType === 'number'
        ? <ol key={key}>{node.children?.map((c: any, i: number) => renderNode(c, i))}</ol>
        : <ul key={key}>{node.children?.map((c: any, i: number) => renderNode(c, i))}</ul>
    case 'listitem':
      return <li key={key}>{node.children?.map((c: any, i: number) => renderLeaf(c, i))}</li>
    case 'quote':
      return <blockquote key={key}>{node.children?.map((c: any, i: number) => renderNode(c, i))}</blockquote>
    default:
      return null
  }
}

function renderLeaf(leaf: any, key: number): React.ReactNode {
  if (!leaf) return null
  if (leaf.type === 'text') {
    let el: React.ReactNode = leaf.text
    if (leaf.format & 1) el = <strong>{el}</strong>
    if (leaf.format & 2) el = <em>{el}</em>
    if (leaf.format & 8) el = <u>{el}</u>
    return <span key={key}>{el}</span>
  }
  return null
}

function NewsSidebarCompact() {
  return (
    <>
      <div className="sidebar-box">
        <h3>DANH MỤC TIN TỨC</h3>
        {categories.map((category) => (
          <a href="#" key={category}><span>{category}</span></a>
        ))}
      </div>
      <div className="sidebar-box tag-box">
        <h3>TỪ KHÓA</h3>
        <div>
          {['Khai giảng', 'Kỹ năng sống', 'Học sinh giỏi', 'Ngoại khóa', 'Đoàn đội', 'Thi đua', 'Bóng đá', 'STEM'].map((tag) => (
            <span key={tag}>{tag}</span>
          ))}
        </div>
      </div>
      <div className="sidebar-box newsletter">
        <h3>ĐĂNG KÝ NHẬN TIN</h3>
        <p>Đăng ký để nhận những tin tức mới nhất từ nhà trường</p>
        <input placeholder="Nhập email của bạn..." />
        <button>Đăng ký</button>
      </div>
    </>
  )
}
